"use client";

/**
 * Hook that turns a two-finger pinch gesture inside a container into a clamped
 * scale value. Single-finger touches are ignored so native vertical scroll is
 * preserved. The browser's default pinch-zoom on the attached element should be
 * suppressed by the caller via `touch-action: pan-y` (Tailwind `touch-pan-y`).
 *
 * Exposes a callback `ref` so the hook re-attaches its listeners whenever the
 * target node mounts (including conditionally-rendered targets). `nodeRef`
 * gives callers a stable accessor when they need to read the DOM node from
 * their own effects.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MutableRefObject } from "react";

interface UsePinchZoomOptions {
  /** Lower clamp for the returned scale */
  min: number;
  /** Upper clamp for the returned scale */
  max: number;
  /** Starting scale (default 1) */
  initial?: number;
  /**
   * When true, the hook adjusts `node.scrollTop` during the gesture so the
   * point under the user's fingers stays visually anchored as the content
   * scales (Google-Calendar-style pinch). Requires the target node to be the
   * scrollable element. Default false.
   */
  anchorScroll?: boolean;
}

interface UsePinchZoomResult<T extends HTMLElement> {
  /** Callback ref to attach to the gesture target. Triggers (re)attach on mount/unmount. */
  ref: (node: T | null) => void;
  /** Stable accessor for the currently-attached node. */
  nodeRef: MutableRefObject<T | null>;
  /** Current scale, clamped to `[min, max]`. */
  scale: number;
}

/**
 * Attach the returned `ref` to a scrollable element to pinch-zoom its content.
 * @param options - min/max clamp and initial scale
 * @returns callback ref to attach to the gesture target, a stable accessor for
 *   the attached node, and the current scale
 */
export function usePinchZoom<T extends HTMLElement>({
  min,
  max,
  initial = 1,
  anchorScroll = false,
}: UsePinchZoomOptions): UsePinchZoomResult<T> {
  // States
  const [scale, setScale] = useState(initial);
  const [node, setNode] = useState<T | null>(null);

  // Refs
  const nodeRef = useRef<T | null>(null);
  const scaleRef = useRef(initial);
  const startDistanceRef = useRef<number | null>(null);
  const startScaleRef = useRef(initial);
  const boundsRef = useRef({ min, max });
  const activePinchRef = useRef(false);
  // Scroll-anchor state captured at touchstart so we can re-position
  // `scrollTop` after each scale change keeps the focal content under the
  // user's fingers. Populated only while a 2-finger gesture is active.
  const anchorRef = useRef<{ contentY: number; viewportY: number; startScale: number } | null>(null);
  const anchorScrollRef = useRef(anchorScroll);

  // Handlers
  const ref = useCallback((next: T | null) => {
    nodeRef.current = next;
    setNode(next);
  }, []);

  // Effects — keep refs in sync without re-binding listeners
  useEffect(() => {
    boundsRef.current = { min, max };
  }, [min, max]);

  useEffect(() => {
    anchorScrollRef.current = anchorScroll;
  }, [anchorScroll]);

  // Effects — attach gesture listeners whenever the target node changes
  useEffect(() => {
    if (!node) return;

    const getDistance = (touches: TouchList): number => {
      if (touches.length < 2) return 0;
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };

    const resetPinch = (): void => {
      activePinchRef.current = false;
      startDistanceRef.current = null;
      anchorRef.current = null;
    };

    /** Capture the focal-point anchor (content Y under the pinch midpoint) for scroll re-anchoring. */
    const captureAnchor = (touches: TouchList): void => {
      if (!anchorScrollRef.current) {
        anchorRef.current = null;
        return;
      }
      const midpointY = (touches[0].clientY + touches[1].clientY) / 2;
      const rect = node.getBoundingClientRect();
      const viewportY = midpointY - rect.top;
      anchorRef.current = {
        contentY: node.scrollTop + viewportY,
        viewportY,
        startScale: scaleRef.current,
      };
    };

    const beginPinch = (touches: TouchList): void => {
      const distance = getDistance(touches);
      if (distance <= 0) {
        resetPinch();
        return;
      }
      activePinchRef.current = true;
      startDistanceRef.current = distance;
      startScaleRef.current = scaleRef.current;
      captureAnchor(touches);
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length === 2) {
        beginPinch(event.touches);
      } else {
        // Any non-2-finger touchstart resets the gesture so a later transition
        // back to 2 fingers anchors against the current pair, not a stale one.
        resetPinch();
      }
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 2) {
        resetPinch();
        return;
      }
      if (!activePinchRef.current || !startDistanceRef.current) {
        beginPinch(event.touches);
        return;
      }
      event.preventDefault();
      const currentDistance = getDistance(event.touches);
      if (currentDistance === 0) return;
      const raw = startScaleRef.current * (currentDistance / startDistanceRef.current);
      const { min: lo, max: hi } = boundsRef.current;
      const clamped = Math.min(hi, Math.max(lo, raw));
      // Sync the ref inline so a follow-up gesture that begins before React
      // commits this setScale still rebases against the *current* scale.
      scaleRef.current = clamped;
      setScale(clamped);
    };

    const handleTouchEnd = (event: TouchEvent) => {
      if (event.touches.length < 2) {
        resetPinch();
        return;
      }
      // ≥2 touches remaining — rebase to the current pair so the next move
      // tick computes the ratio against the still-present fingers.
      beginPinch(event.touches);
    };

    node.addEventListener("touchstart", handleTouchStart, { passive: true });
    node.addEventListener("touchmove", handleTouchMove, { passive: false });
    node.addEventListener("touchend", handleTouchEnd);
    node.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      node.removeEventListener("touchstart", handleTouchStart);
      node.removeEventListener("touchmove", handleTouchMove);
      node.removeEventListener("touchend", handleTouchEnd);
      node.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [node]);

  // Effects — keep the pinch midpoint visually anchored as content scales.
  // useLayoutEffect runs after the parent re-renders the scaled content (and
  // before paint), so the scrollTop adjustment is invisible to the user.
  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const el = nodeRef.current;
    if (!activePinchRef.current || !anchor || !el) return;
    const newContentY = anchor.contentY * (scale / anchor.startScale);
    const maxScrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
    el.scrollTop = Math.min(maxScrollTop, Math.max(0, newContentY - anchor.viewportY));
  }, [scale]);

  return { ref, nodeRef, scale };
}
